import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useChartOfAccounts, ACCOUNT_TYPE_LABELS } from "@/hooks/useChartOfAccounts";

export interface SupplierFormValues {
  name: string;
  document: string;
  phone: string;
  email: string;
  chartAccountId: string;
}

interface SupplierFormFieldsProps {
  values: SupplierFormValues;
  onChange: (values: SupplierFormValues) => void;
  autoFocusName?: boolean;
}

export function SupplierFormFields({
  values,
  onChange,
  autoFocusName = false,
}: SupplierFormFieldsProps) {
  const { getLeafAccounts } = useChartOfAccounts();
  
  // Buscar contas de despesas (custos variáveis, despesas fixas, despesas financeiras)
  const expenseAccounts = [
    ...getLeafAccounts("variable_cost"),
    ...getLeafAccounts("fixed_expense"),
    ...getLeafAccounts("financial_expense"),
  ].sort((a, b) => a.code.localeCompare(b.code));

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div className="space-y-2 sm:col-span-2">
        <Label htmlFor="supplier_name">Nome/Razão social *</Label>
        <Input
          id="supplier_name"
          value={values.name}
          onChange={(e) => onChange({ ...values, name: e.target.value })}
          placeholder="Ex: Distribuidora ABC"
          autoFocus={autoFocusName}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="supplier_document">Documento</Label>
        <Input
          id="supplier_document"
          value={values.document}
          onChange={(e) => onChange({ ...values, document: e.target.value })}
          placeholder="CPF/CNPJ (opcional)"
          inputMode="numeric"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="supplier_phone">Telefone</Label>
        <Input
          id="supplier_phone"
          value={values.phone}
          onChange={(e) => onChange({ ...values, phone: e.target.value })}
          placeholder="(opcional)"
          inputMode="tel"
        />
      </div>

      <div className="space-y-2 sm:col-span-2">
        <Label htmlFor="supplier_email">E-mail</Label>
        <Input
          id="supplier_email"
          value={values.email}
          onChange={(e) => onChange({ ...values, email: e.target.value })}
          placeholder="(opcional)"
          inputMode="email"
        />
      </div>

      <div className="space-y-2 sm:col-span-2">
        <Label>Conta do Plano de Contas</Label>
        <Select
          value={values.chartAccountId}
          onValueChange={(v) => onChange({ ...values, chartAccountId: v })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Selecione uma conta (opcional)" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">(Sem vínculo)</SelectItem>
            {expenseAccounts.map((acc) => (
              <SelectItem key={acc.id} value={acc.id}>
                {acc.code} - {acc.name} ({ACCOUNT_TYPE_LABELS[acc.account_type]})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          Vincule a uma conta para classificação automática no DRE
        </p>
      </div>
    </div>
  );
}
